import React, { useEffect, useState, useCallback, useRef, memo } from "react";
import {
  Box,
  TextField,
  IconButton,
  Grid,
  Button,
  InputAdornment,
  Typography,
  Drawer,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Avatar,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getAllReports } from "../../service/orderService";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import { getAllBranchLists } from "../../service/branchService";
import CountUp from "react-countup"; 


import {
  ShoppingCart as TotalOrdersIcon,
  Cake as CakeOrdersIcon,
  CurrencyRupee as TotalAmountIcon, 
  CheckCircle as ReceivedAmountIcon,
  Pending as PendingAmountIcon,
  DoneAll as CompleteOrdersIcon,
  Cancel as CancelledOrdersIcon,
} from "@mui/icons-material";

const paymentStatusColors = {
  partial: { backgroundColor: "#FF9800", color: "#ffffff" },
  paid: { backgroundColor: "#4CAF50", color: "#ffffff" },
  pending: { backgroundColor: "#F44336", color: "#ffffff" },
};

const orderStatusColors = {
  order_placed: { backgroundColor: "#2196F3", color: "#ffffff" },
  ready_for_delivery: { backgroundColor: "#FFCA28", color: "#000000" },
  completed: { backgroundColor: "#4CAF50", color: "#ffffff" },
  cancelled: { backgroundColor: "#D32F2F", color: "#ffffff" },
};


const cardGradients = [
  "linear-gradient(135deg, #7C0BCC 0%, #4B0082 100%)",
  "linear-gradient(135deg, #FF6F61 0%, #FF3D00 100%)",
  "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
  "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
  "linear-gradient(135deg, #FBC02D 0%, #F57F17 100%)",
  "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)",
  "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
];


const MemoizedCard = memo(({ stat, index }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <Box
      display="flex"
      flex="1 1 auto"
      width={{ xs: "47%", sm: "40%", md: "24.7%" }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: { xs: 80, sm: 100, md: 100 },
          borderRadius: 2,
          boxShadow: "none",
          background: cardGradients[index % cardGradients.length],
          color: "white",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2 }, flex: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography
                variant="body2"
                sx={{
                 fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem", lg: "1.1rem",  },
                 mt: { xs: 0.5, sm: 0.8, md: 1 },
                 lineHeight: 1.2,
                }}
              >
                {stat.title}
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.3rem", lg: "1.5rem",  },
                   mt: { xs: 0.5, sm: 0.8, md: 1 },
                }}
              >
                {stat.prefix || ""}
                <CountUp
                  end={parseFloat(stat.value) || 0}
                  duration={1.5}
                  decimals={stat.prefix ? 2 : 0}
                />
              </Typography>
            </Box>
            <Avatar
              sx={{
                bgcolor: "white",
                width: { xs: 28, sm: 40, md: 50, lg: 60, xl: 70 },
              height: { xs: 28, sm: 40, md: 50, lg: 60, xl: 70 },
              }}
            >
              {stat.icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

const Reports = () => {
  const { showSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [dateErrors, setDateErrors] = useState({ order_date_from: "", order_date_to: "" });
  const [summary, setSummary] = useState({
    total_orders: 0,
    total_cake_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
    total_sales: 0,
    pending_amount: 0,
    received_amount: 0,
  });
  const [branches, setBranches] = useState([]);
  const abortControllerRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));

  const role = localStorage.getItem("role") || "2";
  const branchId = localStorage.getItem("branch") || "";
  const branchName = localStorage.getItem("branch_name") || "Your Branch";

  const statusOptions = [
    { value: "order_placed", label: "Order Placed" },
    { value: "ready_for_delivery", label: "Ready for Delivery" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const initialFilterState = {
    order_number: "",
    status: "",
    order_type: "",
    payment_status: "",
    customer_id: "",
    customer_name: "",
    branch_id: role === "2" ? branchId : "",
    branch_name: role === "2" ? branchName : "",
    order_date_from: null,
    order_date_to: null,
    total_amount_min: "",
    total_amount_max: "",
    notes: "",
    sort_field: "",
    sort_order: "",
  };

  const [filters, setFilters] = useState(initialFilterState);

  // Updated cardData with icons and optional prefix for monetary values
  const cardData = [
    { title: "Total Orders", value: summary.total_orders, icon: <TotalOrdersIcon sx={{ color: Color.primary.main }} /> },
    { title: "Cake Orders", value: summary.total_cake_orders, icon: <CakeOrdersIcon sx={{ color: Color.primary.main }} /> },
    { title: "Total Amount", value: summary.total_sales, icon: <TotalAmountIcon sx={{ color: Color.primary.main }} />,  },
    { title: "Received Amount", value: summary.received_amount, icon: <ReceivedAmountIcon sx={{ color: Color.primary.main }} />,  },
    { title: "Pending Amount", value: summary.pending_amount, icon: <PendingAmountIcon sx={{ color: Color.primary.main }} />,  },
    { title: "Complete Orders", value: summary.completed_orders, icon: <CompleteOrdersIcon sx={{ color: Color.primary.main }} /> },
    { title: "Cancelled Orders", value: summary.cancelled_orders, icon: <CancelledOrdersIcon sx={{ color: Color.primary.main }} /> },
  ];

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await getAllBranchLists();
        if (!res || !res.data) {
          throw new Error("No data returned from branch API");
        }
        const branchData = res.data.data || res.data.branches || res.data || [];
        if (!Array.isArray(branchData)) {
          console.warn("Branch data is not an array:", branchData);
          setBranches([]);
        } else {
          setBranches(branchData);
        }
      } catch (error) {
        console.error("Error fetching branches:", error.message || error);
        showSnackbar("Failed to fetch branches!", "error");
        setBranches([]);
      }
    };
    fetchBranches();
  }, [showSnackbar]);

  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateDates = () => {
    const errors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filters.order_date_from && filters.order_date_from > today) {
      errors.order_date_from = "Order Date cannot be in the future";
    }
    if (filters.order_date_to && filters.order_date_to > today) {
      errors.order_date_to = "Delivery Date cannot be in the future";
    }
    if (filters.order_date_from && filters.order_date_to) {
      if (filters.order_date_from > filters.order_date_to) {
        errors.order_date_to = "Delivery Date must be on or after Order Date";
      }
    }
    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = ({ target: { name, value } }) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (name === "customer_name") {
      setSearch(value);
    }
    if (name === "order_date_from" || name === "order_date_to") {
      setDateErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "branch_id") {
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      fetchOrders();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!validateDates()) {
      showSnackbar("Please fix date errors before searching.", "error");
      return;
    }
    setFiltersApplied(true);
    setDrawerOpen(false);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    fetchOrders();
  };

  const handleReset = () => {
    setFilters({
      ...initialFilterState,
      branch_id: role === "2" ? branchId : "",
      branch_name: role === "2" ? branchName : "",
    });
    setSearch("");
    setDebouncedSearch("");
    setDateErrors({ order_date_from: "", order_date_to: "" });
    setPaginationModel({ page: 0, pageSize: 10 });
    setFiltersApplied(false);
    fetchOrders();
  };

  const handleClear = () => {
    setSearch("");
    setDebouncedSearch("");
    setFilters((prev) => ({
      ...prev,
      customer_name: "",
      branch_id: role === "2" ? branchId : "",
      branch_name: role === "2" ? branchName : "",
    }));
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    fetchOrders();
  };

  const fetchOrders = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const searchPayload = {
        ...filters,
        customer_name: debouncedSearch || filters.customer_name,
        order_date_from: formatDate(filters.order_date_from),
        order_date_to: formatDate(filters.order_date_to),
        sort_field: sortModel.length > 0 ? sortModel[0].field : "id",
        sort_order: sortModel.length > 0 ? sortModel[0].sort : "desc",
        branch_id: filters.branch_id,
      };

      if (!searchPayload.branch_id) {
        delete searchPayload.branch_id;
      }

      const res = await getAllReports(
        { ...searchPayload, page, limit },
        {
          signal: abortControllerRef.current.signal,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (res?.data?.summary) {
        setSummary({
          total_orders: res.data.summary.total_orders || 0,
          total_cake_orders: res.data.summary.total_cake_orders || 0,
          completed_orders: res.data.summary.completed_orders || 0,
          cancelled_orders: res.data.summary.cancelled_orders || 0,
          total_sales: res.data.summary.total_sales || 0,
          received_amount: res.data.summary.received_amount || 0,
          pending_amount: res.data.summary.pending_amount || 0,
        });
      } else {
        setSummary({
          total_orders: 0,
          total_cake_orders: 0,
          completed_orders: 0,
          cancelled_orders: 0,
          total_sales: 0,
          received_amount: 0,
          pending_amount: 0,
        });
      }

      const data = res?.data?.data;
      if (Array.isArray(data?.data)) {
        const normalizedRows = data.data.map((row) => ({
          ...row,
          customerName: row.customer?.name || row.customer_name || "",
          branchName: row.branch?.name || row.branch_name || "",
          order_date: row.order_date ? row.order_date.split("T")[0] : "",
        }));
        setRows(normalizedRows);
        setRowCount(data.total || 0);
      } else {
        setRows([]);
        setRowCount(0);
        showSnackbar("No orders found or unexpected data format!", "info");
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("API request cancelled");
      } else {
        console.error("Error fetching orders:", error);
        showSnackbar(error?.response?.data?.message || "Failed to fetch orders!", "error");
        setRows([]);
        setRowCount(0);
        setSummary({
          total_orders: 0,
          total_cake_orders: 0,
          completed_orders: 0,
          cancelled_orders: 0,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [filters, paginationModel, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchOrders();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchOrders]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const columns = [
    { field: "order_number", headerName: "Order No", flex: 1, minWidth: isMobile ? 100 : 150 },
    { field: "customer_name", headerName: "Customer", flex: 1, minWidth: isMobile ? 120 : 150 },
    { field: "branchName", headerName: "Branch", flex: 1, minWidth: isMobile ? 120 : 150 },
    { field: "order_date", headerName: "Date", flex: 1, minWidth: 180 },
    { field: "total_amount", headerName: "Total", flex: 0.8, minWidth: isMobile ? 80 : 120 },
    { field: "items_count", headerName: "Total Items", flex: 0.8, minWidth: isMobile ? 80 : 120 },
    {
      field: "payment_status",
      headerName: "Payment Status",
      flex: 1,
      minWidth: isMobile ? 140 : 150,
      renderCell: (params) => {
        const status = params.value || "pending";
        const { backgroundColor, color } = paymentStatusColors[status] || paymentStatusColors.pending;
        return (
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            sx={{
              backgroundColor,
              color,
              fontSize: isMobile ? "0.75rem" : "0.85rem",
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Order Status",
      flex: 1,
      minWidth: isMobile ? 140 : 140,
      renderCell: (params) => {
        const status = params.value || "order_placed";
        const { backgroundColor, color } = orderStatusColors[status] || orderStatusColors.order_placed;
        return (
          <Chip
            label={statusOptions.find((option) => option.value === status)?.label || status}
            sx={{
              backgroundColor,
              color,
              fontSize: isMobile ? "0.75rem" : "0.85rem",
              fontWeight: 500,
            }}
          />
        );
      },
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1, md: 6 }} sx={{ width: "100%", maxWidth: "100%", padding: isMobile ? 1 : 0 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
        sx={{ width: "100%", padding: 0 }}
      >
        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : "10px", width: isMobile ? "100%" : "auto" }}>
          <TextField
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setFilters((prev) => ({ ...prev, customer_name: e.target.value }));
            }}
            placeholder="Search customer name"
            variant="outlined"
            InputLabelProps={{ shrink: false }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClear}>
                    <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": { display: "none" },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                height: isMobile ? "34px" : "37.69px",
                fontSize: isMobile ? "0.85rem" : "1rem",
              },
              width: isMobile ? "100%" : 250,
              mb: isMobile ? 0 : 0,
            }}
          />
          {role === "1" && (
            <FormControl
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                  height: isMobile ? "34px" : "37.69px",
                  fontSize: isMobile ? "0.85rem" : "1rem",
                },
                width: isMobile ? "100%" : 200,
              }}
            >
              <Select
                name="branch_id"
                value={filters.branch_id}
                onChange={handleChange}
                displayEmpty
                renderValue={(value) =>
                  value
                    ? branches.find((branch) => branch.id === value)?.name || "Select Branch"
                    : "All Branches"
                }
              >
                <MenuItem value="">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={{ gap: isMobile ? 1 : 2 }}>
          <IconButton
            sx={{
              color: Color.secondary.main,
              fontSize: isMobile ? "26px" : "30px",
              border: "1px solid",
              borderColor: Color.secondary.main,
              borderRadius: "4px",
              height: isMobile ? "34px" : "37px",
              width: isMobile ? "34px" : "37px",
            }}
            onClick={() => setDrawerOpen(true)}
          >
            <FilterAltIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Updated Card Rendering Section */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        sx={{ width: { xs: "100%", sm: "100%", md: "100%" }, mt: 2 }}
      >
        {cardData.map((card, index) => (
          <MemoizedCard key={card.title} stat={card} index={index} />
        ))}
      </Box>

      <Box sx={{ width: "100%", mt: 2 }}>
        {rows.length === 0 && !loading && (
          <Box sx={{ textAlign: "center", p: 2 }}>
            <Typography sx={{ fontSize: isMobile ? "0.9rem" : "1rem", color: "text.secondary" }}>
              No orders found for the search term or filters.
            </Typography>
          </Box>
        )}
        <DataGrid
          rows={rows.filter(
            (row) =>
              row.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
              row.order_number?.toLowerCase().includes(search.toLowerCase()) ||
              row.items_count?.toString().includes(search.toLowerCase())
          )}
          columns={columns}
          rowHeight={isMobile ? 48 : 56}
          loading={loading}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "18px",
              fontWeight: 500,
              pl: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: 400,
              pl: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setDrawerOpen(false);
          }
        }}
        PaperProps={{
          sx: {
            width: isMobile ? "60vw" : 480,
            boxShadow: 4,
            borderRadius: "8px 0 0 8px",
          },
        }}
        sx={{
          zIndex: 1300,
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(129, 128, 128, 0.05)",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1.5}
            mt={{ xs: 0, md: 1 }}
            borderBottom="1px solid #e0e0e0"
          >
            <Typography fontSize={isMobile ? 16 : 18} fontWeight={600}>
              Filter Orders
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
            </IconButton>
          </Box>

          <Box p={2} sx={{ flexGrow: 1, overflowY: "auto" }}>
            <form onSubmit={handleSearch}>
              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: "bold",
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    Branch
                  </Typography>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "142%" : 215,
                      },
                    }}
                  >
                    <Select
                      name="branch_id"
                      value={filters.branch_id}
                      onChange={handleChange}
                      displayEmpty
                      disabled={role === "2"}
                    >
                      {role === "1" && <MenuItem value="">All Branches</MenuItem>}
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: "bold",
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    Customer Name
                  </Typography>
                  <TextField
                    fullWidth
                    name="customer_name"
                    value={filters.customer_name}
                    onChange={handleChange}
                    placeholder="Search customer name"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "100%" : 215,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: "bold",
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    Order Number
                  </Typography>
                  <TextField
                    fullWidth
                    name="order_number"
                    value={filters.order_number}
                    onChange={handleChange}
                    placeholder="Search order number"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "100%" : 215,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: "bold",
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    Order Type
                  </Typography>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "111%" : 215,
                      },
                    }}
                  >
                    <Select
                      name="order_type"
                      value={filters.order_type}
                      onChange={handleChange}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Order Type
                      </MenuItem>
                      <MenuItem value="invoice">Invoice</MenuItem>
                      <MenuItem value="quotation">Quotation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: "bold",
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    Order Status
                  </Typography>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "105%" : 215,
                      },
                    }}
                  >
                    <Select
                      name="status"
                      value={filters.status}
                      onChange={handleChange}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Order Status
                      </MenuItem>
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={isExtraSmall ? 12 : 6}>
                    <Typography
                      sx={{
                        mb: 0.5,
                        fontWeight: "bold",
                        color: Color.label.main,
                        fontSize: isMobile ? "12px" : "13px",
                      }}
                    >
                      Order Date(From)
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={filters.order_date_from}
                        onChange={(newValue) =>
                          handleChange({
                            target: { name: "order_date_from", value: newValue },
                          })
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                            InputLabelProps: { shrink: false },
                            size: "small",
                            sx: {
                              "& .MuiInputLabel-root": { display: "none" },
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                                height: isMobile ? "34px" : "37.69px",
                                fontSize: isMobile ? "0.85rem" : "1rem",
                              },
                              width: isMobile ? "100%" : 215,
                            },
                            error: !!dateErrors.order_date_from,
                            helperText: dateErrors.order_date_from,
                          },
                        }}
                        format="yyyy-MM-dd"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={isExtraSmall ? 12 : 6}>
                    <Typography
                      sx={{
                        mb: 0.5,
                        fontWeight: "bold",
                        color: Color.label.main,
                        fontSize: isMobile ? "12px" : "13px",
                      }}
                    >
                      Order Date(To)
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={filters.order_date_to}
                        onChange={(newValue) =>
                          handleChange({
                            target: { name: "order_date_to", value: newValue },
                          })
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                            InputLabelProps: { shrink: false },
                            size: "small",
                            sx: {
                              "& .MuiInputLabel-root": { display: "none" },
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                                height: isMobile ? "34px" : "37.69px",
                                fontSize: isMobile ? "0.85rem" : "1rem",
                              },
                              width: isMobile ? "100%" : 215,
                            },
                            error: !!dateErrors.order_date_to,
                            helperText: dateErrors.order_date_to,
                          },
                        }}
                        format="yyyy-MM-dd"
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Grid>

              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="left"
                gap={isMobile ? 1 : 2}
                mt={isMobile ? 2 : 4}
              >
                <Button
                  type="submit"
                  sx={{
                    background: Color.primary.main,
                    color: "white",
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 139,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                    "&:hover": {
                      background: Color.primary.dark,
                    },
                  }}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
                <Button
                  onClick={handleReset}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 138,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Reset
                </Button>
                <Button
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 138,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Close
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Reports;